import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  ArrowLeftIcon,
  CaretIcon,
  CheckmarkIcon,
  StatusInProgressIcon,
  StatusPendingIcon,
  StatusAssignedIcon,
  PaperclipIcon,
  DownloadIcon,
  SendIcon,
} from '../../assets/icons';
import elena from '../../assets/avatars/elena-rodriguez.png';
import david from '../../assets/avatars/david-chen.png';
import sarah from '../../assets/avatars/sarah-jenkins.png';
import admin from '../../assets/avatars/admin-user.png';

type SubStatus = 'completed' | 'inprogress' | 'pending' | 'assigned';

interface SubTaskState {
  name?: string; // engagement name
  taskName?: string;
  sub?: {
    name: string;
    detail: string;
    priority: string;
    status: SubStatus;
    assignee: string | null;
    avatar?: string;
    deadline: string;
  };
}

const ACTIVITY = [
  { title: 'Task Created', sub: 'By Administrator', time: 'OCT 12 • 09:15 AM' },
  { title: 'Team Assigned', sub: 'Ravi and Anita joined task', time: 'OCT 12 • 10:45 AM' },
  { title: 'Reminder Sent', sub: 'Automated nudge to Ravi', time: 'OCT 14 • 08:00 AM', auto: true },
  { title: 'Status Updated', sub: "Moved to 'In Progress'", time: 'OCT 14 • 11:20 AM' },
];

const STATUS_META: Record<SubStatus, { label: string; className: string }> = {
  completed: { label: 'Completed', className: 'bg-[#dbeafe] text-[#2563eb]' },
  inprogress: { label: 'In Progress', className: 'bg-[#e0e7ff] text-[#0f52ba]' },
  pending: { label: 'Pending', className: 'bg-[#f3f4f6] text-[#6b7280]' },
  assigned: { label: 'Assigned', className: 'bg-[#eef4ff] text-[#4b5563]' },
};

function StatusPill({ status }: { status: SubStatus }) {
  const meta = STATUS_META[status];
  const icon =
    status === 'completed' ? (
      <CheckmarkIcon className="size-3" />
    ) : status === 'inprogress' ? (
      <StatusInProgressIcon className="size-3.5" />
    ) : status === 'pending' ? (
      <StatusPendingIcon className="size-3.5" />
    ) : (
      <StatusAssignedIcon className="size-3.5" />
    );
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
        meta.className,
      ].join(' ')}
    >
      {icon}
      {meta.label}
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.55px] text-[#64748b]">
        {label}
      </span>
      {children}
    </div>
  );
}

export default function SubTaskDetailPage() {
  const navigate = useNavigate();
  const { id, taskId } = useParams();
  const location = useLocation();
  const state = (location.state as SubTaskState | null) ?? {};

  const engagementName = state.name ?? 'Guru Poornima';
  const taskName = state.taskName ?? 'Blood Donation Camp';
  const sub = state.sub ?? {
    name: 'Arrange Medical Team',
    detail: 'Emergency protocols review',
    priority: 'HIGH',
    status: 'inprogress' as SubStatus,
    assignee: 'Dr. Marcus Thorne',
    avatar: david,
    deadline: 'Oct 28, 2023',
  };

  const engagementPath = `/user/engagements/${id ?? ''}`;
  const taskPath = `/user/engagements/${id ?? ''}/tasks/${taskId ?? ''}`;

  const [status, setStatus] = useState<SubStatus>(sub.status);
  const statusItems: MenuProps['items'] = (
    ['completed', 'inprogress', 'pending', 'assigned'] as SubStatus[]
  ).map((s) => ({ key: s, label: STATUS_META[s].label }));

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs
        items={[
          { label: 'Engagements', to: '/user/engagements' },
          { label: engagementName, to: engagementPath },
          { label: taskName, to: taskPath },
          { label: sub.name },
        ]}
      />

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate(taskPath, { state: { name: engagementName, taskName } })}
            className="text-ink transition-colors hover:text-brand"
          >
            <ArrowLeftIcon className="size-7" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-2xl font-semibold text-ink">{sub.name}</h1>
            <span className="text-sm text-ink-secondary">{sub.detail}</span>
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      {/* Overview */}
      <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          <Field label="Priority">
            <span className="text-sm font-semibold text-[#1f2937]">{sub.priority}</span>
          </Field>
          <Field label="Status">
            <Dropdown
              menu={{
                items: statusItems,
                selectable: true,
                selectedKeys: [status],
                onClick: ({ key }) => setStatus(key as SubStatus),
              }}
              trigger={['click']}
            >
              <button type="button" className="flex items-center gap-1.5 self-start">
                <StatusPill status={status} />
                <CaretIcon className="size-4 text-ink-secondary" />
              </button>
            </Dropdown>
          </Field>
          <Field label="Assigned To">
            {sub.assignee ? (
              <div className="flex items-center gap-2">
                <img
                  src={sub.avatar ?? elena}
                  alt={sub.assignee}
                  className="size-6 shrink-0 rounded-full object-cover"
                />
                <span className="text-sm font-medium text-[#334155]">{sub.assignee}</span>
              </div>
            ) : (
              <span className="text-sm italic text-[#9ca3af]">Unassigned</span>
            )}
          </Field>
          <Field label="Due Date">
            <span className="text-sm font-medium text-[#334155]">{sub.deadline}</span>
          </Field>
        </div>

        <div className="mt-6 border-t border-[#f3f4f6] pt-6">
          <h3 className="text-sm font-semibold text-ink">Description</h3>
          <p className="mt-2 max-w-[720px] text-sm leading-[22px] text-ink-secondary">
            Coordinate with the City General Hospital to secure ambulance units and on-site medical
            staff for the event. Review emergency protocols, confirm coverage windows, and log the
            final roster before the deadline.
          </p>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Documentation */}
          <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
            <div className="flex items-center gap-2">
              <PaperclipIcon className="size-5 text-[#0f172a]" />
              <h2 className="text-base font-semibold text-[#0f172a]">Documentation</h2>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg bg-brand-shade p-4">
              <div className="flex items-center gap-4">
                <span className="flex size-10 items-center justify-center rounded bg-white text-xs font-bold text-[#ef4444] shadow-card">
                  PDF
                </span>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-[#0f172a]">medical_v2.pdf</span>
                  <span className="text-xs text-[#64748b]">2.4 MB • PDF</span>
                </div>
              </div>
              <button
                type="button"
                aria-label="Download"
                className="text-[#64748b] transition-colors hover:text-brand"
              >
                <DownloadIcon className="size-5" />
              </button>
            </div>
          </div>

          {/* Comments */}
          <div className="flex flex-col gap-6 rounded-xl border border-line-soft bg-white p-6 shadow-card">
            <h2 className="text-base font-semibold text-ink">Comments</h2>
            <div className="flex gap-4">
              <img src={admin} alt="Ravi Kumar" className="size-10 shrink-0 rounded-full object-cover" />
              <div className="flex flex-1 flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-ink">Ravi Kumar</span>
                  <span className="text-xs font-medium text-ink-secondary">2 hours ago</span>
                </div>
                <div className="rounded-lg border border-[#f3f4f6] bg-[#f8fafc] p-4 text-sm leading-[22px] text-ink">
                  I&apos;ve reached out to the City General Hospital. They&apos;ve agreed to provide 5
                  ambulance units for the event day. Waiting on doctor confirmation.
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <img src={sarah} alt="You" className="size-10 shrink-0 rounded-full object-cover" />
              <div className="relative flex-1">
                <textarea
                  rows={3}
                  placeholder="Add a comment or update..."
                  className="w-full resize-none rounded-lg border border-[#e5e7eb] bg-[#f8fafc] p-4 pr-14 text-sm text-ink placeholder:text-ink-secondary focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
                />
                <button
                  type="button"
                  aria-label="Send comment"
                  className="absolute bottom-3 right-3 flex size-8 items-center justify-center rounded-lg bg-brand text-white shadow-btn transition-colors hover:bg-brand-dark"
                >
                  <SendIcon className="size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Assignee */}
          <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-ink">Assignee</h2>
              <button type="button" className="text-sm font-medium text-brand hover:underline">
                View list
              </button>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="relative shrink-0">
                <img
                  src={sub.avatar ?? david}
                  alt={sub.assignee ?? 'Assignee'}
                  className="size-12 rounded-full border-2 border-white object-cover shadow-card"
                />
                <span className="absolute bottom-0 right-0 size-3 rounded-full border-2 border-white bg-[#22c55e]" />
              </span>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-ink">{sub.assignee ?? 'Unassigned'}</span>
                <span className="text-xs font-semibold text-brand">Lead Coordinator</span>
              </div>
            </div>
          </div>

          {/* Activity History */}
          <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
            <h2 className="text-base font-semibold text-ink">Activity History</h2>
            <div className="mt-6 flex flex-col gap-6 border-l-2 border-[#f3f4f6] pl-5">
              {ACTIVITY.map((a, i) => {
                const active = i === ACTIVITY.length - 1;
                return (
                  <div key={a.title} className="relative">
                    <span
                      className={[
                        'absolute rounded-full border-2 border-white',
                        active
                          ? '-left-[27px] top-0.5 size-4 bg-brand shadow-[0px_0px_0px_2px_rgba(15,82,186,0.2)]'
                          : '-left-[25px] top-1 size-3 bg-[#e5e7eb]',
                      ].join(' ')}
                    />
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={['text-sm font-medium', active ? 'text-brand' : 'text-ink'].join(' ')}
                        >
                          {a.title}
                        </span>
                        {a.auto && (
                          <span className="rounded bg-brand px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.45px] text-white">
                            Auto
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-[#64748b]">{a.sub}</span>
                      <span className="text-[10px] font-medium uppercase tracking-[0.25px] text-[#9ca3af]">
                        {a.time}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
